/**
 * Activity Log Service
 * Fetches chronological activity for a contact including:
 * - Notes added
 * - Support tasks created/resolved
 * - Interactions logged
 * - Event participation
 * - Messages sent (campaigns/one-offs)
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import { TASK_TYPE_LABELS } from "../types/supportTasks.types";
import type { SupportTaskType } from "../types/supportTasks.types";

export interface ActivityLogItem {
  id: string;
  type:
    | "note"
    | "task_created"
    | "task_resolved"
    | "interaction"
    | "event"
    | "message"
    | "tag_added"
    | "tag_removed";
  title: string;
  description?: string;
  timestamp: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  metadata?: Record<string, any>;
  link?: {
    type: "task" | "interaction" | "event" | "note";
    id: string;
  };
}

/**
 * Get activity log for a contact
 * Combines data from multiple tables into a unified timeline
 */
export const getContactActivityLog = async (
  contactId: UUID,
  limit: number = 50,
): Promise<ActivityLogItem[]> => {
  const activities: ActivityLogItem[] = [];

  // 1. Fetch contact notes
  const { data: notes, error: notesError } = await supabase
    .from("contact_notes")
    .select(
      `
      id,
      content,
      created_at,
      created_by,
      creator:profiles!contact_notes_created_by_fkey(id, full_name, email)
    `,
    )
    .eq("contact_id", contactId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (!notesError && notes) {
    for (const note of notes) {
      activities.push({
        id: `note-${note.id}`,
        type: "note",
        title: "Note added",
        description:
          note.content.length > 100
            ? note.content.substring(0, 100) + "..."
            : note.content,
        timestamp: note.created_at,
        user: note.creator
          ? {
              id: note.creator.id,
              name: note.creator.full_name || note.creator.email,
              email: note.creator.email,
            }
          : undefined,
        link: { type: "note", id: note.id },
      });
    }
  }

  // 2. Fetch support tasks
  const { data: tasks, error: tasksError } = await supabase
    .from("support_tasks")
    .select(
      `
      id,
      task_type,
      status,
      notes,
      tags,
      created_at,
      completed_at,
      created_by,
      creator:profiles!support_tasks_created_by_fkey(id, full_name, email),
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email)
    `,
    )
    .eq("contact_id", contactId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (!tasksError && tasks) {
    for (const task of tasks) {
      // Task created event
      activities.push({
        id: `task-created-${task.id}`,
        type: "task_created",
        title: `Support task created: ${formatTaskType(task.task_type)}`,
        description: task.agent ? `Assigned to ${task.agent.name}` : undefined,
        timestamp: task.created_at,
        user: task.creator
          ? {
              id: task.creator.id,
              name: task.creator.full_name || task.creator.email,
              email: task.creator.email,
            }
          : undefined,
        link: { type: "task", id: task.id },
        metadata: { taskType: task.task_type, status: task.status },
      });

      // Task resolved event (if completed)
      if (
        task.completed_at &&
        (task.status === "completed" || task.status === "cancelled")
      ) {
        activities.push({
          id: `task-resolved-${task.id}`,
          type: "task_resolved",
          title: `Support task ${task.status}: ${formatTaskType(task.task_type)}`,
          description: task.notes,
          timestamp: task.completed_at,
          user: task.agent
            ? {
                id: task.agent.id,
                name: task.agent.name,
                email: task.agent.email,
              }
            : undefined,
          link: { type: "task", id: task.id },
          metadata: {
            taskType: task.task_type,
            status: task.status,
            tags: task.tags,
          },
        });
      }
    }
  }

  // 3. Fetch interactions
  const { data: interactions, error: interactionsError } = await supabase
    .from("interactions")
    .select(
      `
      id,
      type,
      method,
      subject,
      description,
      interaction_date,
      outcome,
      created_at,
      created_by,
      creator:profiles!interactions_created_by_fkey(id, full_name, email)
    `,
    )
    .eq("contact_id", contactId)
    .order("interaction_date", { ascending: false })
    .limit(limit);

  if (!interactionsError && interactions) {
    for (const interaction of interactions) {
      const interactionDesc =
        interaction.description || interaction.subject || "";
      activities.push({
        id: `interaction-${interaction.id}`,
        type: "interaction",
        title: `Interaction: ${formatInteractionType(interaction.type || interaction.method)}`,
        description:
          interactionDesc.length > 100
            ? interactionDesc.substring(0, 100) + "..."
            : interactionDesc,
        timestamp: interaction.interaction_date || interaction.created_at,
        user: interaction.creator
          ? {
              id: interaction.creator.id,
              name: interaction.creator.full_name || interaction.creator.email,
              email: interaction.creator.email,
            }
          : undefined,
        link: { type: "interaction", id: interaction.id },
        metadata: {
          interactionType: interaction.type,
          method: interaction.method,
          outcome: interaction.outcome,
        },
      });
    }
  }

  // 4. Fetch event participation (event_attendees)
  const { data: eventAttendees, error: eventsError } = await supabase
    .from("event_attendees")
    .select(
      `
      id,
      attendance_status,
      checked_in_at,
      created_at,
      event:events!event_attendees_event_id_fkey(id, title, start_date)
    `,
    )
    .eq("contact_id", contactId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (!eventsError && eventAttendees) {
    for (const attendee of eventAttendees) {
      // A to-one embed (event_attendees.event_id -> events) arrives as an object; the client's inferred
      // type says array. The column is events.title: events has no `name`, which made this request
      // fail with 400 on every contact (gap list M7).
      const event = attendee.event as unknown as {
        id: string;
        title: string;
      } | null;
      if (event) {
        activities.push({
          id: `event-${attendee.id}`,
          type: "event",
          title: `Event: ${event.title}`,
          description: attendee.checked_in_at
            ? `Checked in at ${new Date(attendee.checked_in_at).toLocaleString()}`
            : `Status: ${attendee.attendance_status || "registered"}`,
          timestamp: attendee.checked_in_at || attendee.created_at,
          link: { type: "event", id: event.id },
          metadata: {
            eventName: event.title,
            status: attendee.attendance_status,
          },
        });
      }
    }
  }

  // Sort all activities by timestamp (most recent first)
  activities.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  // Return limited results
  return activities.slice(0, limit);
};

// Helper functions
function formatTaskType(taskType: string): string {
  const labels: Record<string, string> = {
    birthday_message: "Birthday Message",
    "28_day_past_due_legacy_retention": "28 Day Past Due",
  };
  // Fall back to the shared label map so every task type (e.g. the Hard Decline Outreach Day 1/2/7
  // cadence) reads as its label in the activity timeline instead of the raw wire value.
  return (
    labels[taskType] ||
    TASK_TYPE_LABELS[taskType as SupportTaskType] ||
    taskType
  );
}

function formatInteractionType(interactionType: string | null): string {
  if (!interactionType) {
    return "Interaction";
  }
  const labels: Record<string, string> = {
    email: "Email",
    phone_call: "Phone Call",
    phone: "Phone Call",
    meeting: "Meeting",
    in_person: "In Person",
    note: "Note",
    sms: "SMS",
    text: "Text Message",
    social_media: "Social Media",
    chat: "Chat",
    form_submission: "Form Submission",
    document_shared: "Document Shared",
    video_call: "Video Call",
    other: "Other",
    voucher_issued: "Voucher Issued",
    offer_link_sent: "Offer Link Sent",
    offer_link_copied: "Offer Link Shared",
    physical_distributed: "Reward Item Handed Out",
    physical_shipped: "Reward Item Shipped",
  };
  return labels[interactionType.toLowerCase()] || interactionType;
}
